import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import './Login_admin.css';

const LoginAdmin = () => {
  const navigate = useNavigate();
  const captchaRef = useRef(null);

  const [paso, setPaso] = useState('credenciales'); // 'credenciales' | 'setup_totp' | 'codigo_totp'
  const [nombreUsuario, setNombreUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [secretoManual, setSecretoManual] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!nombreUsuario.trim() || !password.trim()) {
      setErrorMessage('Por favor, complete todos los campos.');
      return;
    }

    if (!captchaToken) {
      setErrorMessage('Por favor, confirma que no eres un robot.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_ADMIN_URL}/login.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre_usuario: nombreUsuario.trim(),
          password: password.trim(),
          captcha_token: captchaToken
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.requiere_setup_totp) {
          await iniciarSetupTotp(data.nombre_usuario);
        } else {
          setPaso('codigo_totp');
        }
      } else {
        setErrorMessage(data.message || 'Credenciales invalidas');
      }
    } catch (error) {
      console.error('Error en login admin:', error);
      setErrorMessage('Error de conexion');
    } finally {
      setIsLoading(false);
      setCaptchaToken(null);
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    }
  };

  const iniciarSetupTotp = async (usuario) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_ADMIN_URL}/setup_totp.php?nombre_usuario=${encodeURIComponent(usuario)}`,
        { method: 'GET', credentials: 'include' }
      );
      const data = await response.json();

      if (data.success) {
        setSecretoManual(data.secreto_manual);
        setOtpauthUrl(data.otpauth_url);
        setPaso('setup_totp');
      } else {
        setErrorMessage(data.message || 'No se pudo iniciar la configuracion del 2FA');
      }
    } catch (error) {
      console.error('Error en setup_totp:', error);
      setErrorMessage('Error de conexion');
    }
  };

  const handleConfirmarSetup = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!codigo.trim()) {
      setErrorMessage('Ingresa el codigo de 6 digitos');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_ADMIN_URL}/setup_totp.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre_usuario: nombreUsuario.trim(),
          codigo: codigo.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setCodigo('');
        setPaso('codigo_totp');
      } else {
        setErrorMessage(data.message || 'Codigo incorrecto');
      }
    } catch (error) {
      console.error('Error confirmando setup 2FA:', error);
      setErrorMessage('Error de conexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificarTotp = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!codigo.trim()) {
      setErrorMessage('Ingresa el codigo de 6 digitos');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_ADMIN_URL}/verify_totp.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo: codigo.trim() })
      });

      const data = await response.json();

      if (data.success) {
        navigate('/admin');
      } else {
        setErrorMessage(data.message || 'Codigo incorrecto');
      }
    } catch (error) {
      console.error('Error en verify_totp:', error);
      setErrorMessage('Error de conexion');
    } finally {
      setIsLoading(false);
      setCodigo('');
    }
  };

  return (
    <div className="la-body-wrapper">
      <div className="la-login-container">
        <div className="la-logo-section">
          <div className="la-logo-text">Ulalaaa</div>
          <div className="la-subtitle">Panel de Administracion</div>
        </div>

        {errorMessage && (
          <div className="la-error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {errorMessage}
          </div>
        )}

        {paso === 'credenciales' && (
          <form onSubmit={handleLogin}>
            <div className="la-form-group">
              <label htmlFor="nombre_usuario">
                <i className="fas fa-user"></i> Usuario
              </label>
              <input
                type="text"
                id="nombre_usuario"
                value={nombreUsuario}
                onChange={(e) => setNombreUsuario(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="la-form-group">
              <label htmlFor="password">
                <i className="fas fa-lock"></i> Contrasena
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="la-captcha-container">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
                theme="dark"
                hl="es"
              />
            </div>

            <button type="submit" className="la-login-btn" disabled={isLoading}>
              {isLoading ? 'Verificando...' : 'INGRESAR'}
            </button>
          </form>
        )}

        {paso === 'setup_totp' && (
          <form onSubmit={handleConfirmarSetup}>
            <p className="la-info-text">
              Configura tu aplicacion de autenticacion (Google Authenticator, Microsoft Authenticator o Duo Mobile) escaneando este codigo, o ingresando la clave manualmente.
            </p>

            <div className="la-secreto-manual">
              <span>{secretoManual}</span>
            </div>

            <div className="la-form-group">
              <label htmlFor="codigo_setup">
                <i className="fas fa-shield-alt"></i> Codigo de 6 digitos
              </label>
              <input
                type="text"
                id="codigo_setup"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                maxLength={6}
                required
                disabled={isLoading}
                autoComplete="off"
              />
            </div>

            <button type="submit" className="la-login-btn" disabled={isLoading}>
              {isLoading ? 'Confirmando...' : 'CONFIRMAR 2FA'}
            </button>
          </form>
        )}

        {paso === 'codigo_totp' && (
          <form onSubmit={handleVerificarTotp}>
            <p className="la-info-text">
              Ingresa el codigo de 6 digitos de tu aplicacion de autenticacion.
            </p>

            <div className="la-form-group">
              <label htmlFor="codigo_totp">
                <i className="fas fa-shield-alt"></i> Codigo de verificacion
              </label>
              <input
                type="text"
                id="codigo_totp"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                maxLength={6}
                required
                disabled={isLoading}
                autoComplete="off"
                autoFocus
              />
            </div>

            <button type="submit" className="la-login-btn" disabled={isLoading}>
              {isLoading ? 'Verificando...' : 'VERIFICAR'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginAdmin;